import { DateTime } from "@/util";
import { downloadAttachment } from "@/api/williams/renewals";
import { ExpandableCard } from "../../components";
import { faChevronDown, faChevronUp, faPaperclip } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TestResult, WelderUpdate } from "@/api/williams/welders";
import { useState } from "react";
import { useWelderUpdates } from "../hooks";

export type WelderUpdatesProps = {
  welderId: string;
  expanded?: boolean;
};

export function WelderUpdates({ welderId, ...props }: WelderUpdatesProps) {
  const updates = useWelderUpdates(welderId);

  return (
    <ExpandableCard caption={`Updates (${updates.data?.total ?? 0})`} expanded={props.expanded}>
      {updates.isLoading ? (
        <p>Loading...</p>
      ) : updates.isError ? (
        <p>An unknown error occurred.</p>
      ) : updates.isSuccess && updates.data.results.length > 0 ? (
        <WelderUpdatesTable data={updates.data.results} />
      ) : (
        <p>No updates found.</p>
      )}
    </ExpandableCard>
  );
}

function WelderUpdatesTable({ data }: { data: WelderUpdate[] }) {
  return (
    <table className="table table-sm table-borderless mb-0">
      <thead>
        <tr>
          <th className="col col-md-3">Process-technique</th>
          <th className="col-3 d-none d-md-table-cell">Criteria</th>
          <th className="col-3 d-none d-md-table-cell">Result</th>
          <th className="col-3 d-none d-md-table-cell">Weld Date</th>
          <th className="col-auto" />
        </tr>
      </thead>
      <tbody>
        {data.map((update) => (
          <WelderUpdatesRow key={update.updateId} update={update} />
        ))}
      </tbody>
    </table>
  );
}

function WelderUpdatesRow({ update }: { update: WelderUpdate }) {
  const [expanded, setExpanded] = useState<boolean>(false);

  return (
    <>
      <tr key={update.updateId}>
        <td>{`${update.processName}-${update.processType}`}</td>
        <td className="d-none d-md-table-cell">{update.acceptanceCriteria}</td>
        <td className="d-none d-md-table-cell">
          {update.result === TestResult.Accept ? (
            <span className="badge bg-success lh-sm">Pass</span>
          ) : (
            <span className="badge bg-danger lh-sm">Fail</span>
          )}
        </td>
        <td className="d-none d-md-table-cell">{DateTime.getLocalDate(update.weldDate)}</td>
        <td className="text-end text-nowrap">
          <FontAwesomeIcon
            role="button"
            icon={expanded ? faChevronUp : faChevronDown}
            size="sm"
            className="me-1"
            title="Details"
            onClick={() => setExpanded((prev) => !prev)}
            fixedWidth={true}
          />
          <FontAwesomeIcon
            role="button"
            icon={faPaperclip}
            size="sm"
            className="me-1"
            title="Download Attachmment"
            onClick={() => downloadAttachment(update.updateId)}
            fixedWidth={true}
            style={{
              opacity: update.hasAttachments ? 1 : 0.3,
              pointerEvents: update.hasAttachments ? "auto" : "none",
            }}
          />
        </td>
      </tr>
      {expanded && (
        <tr>
          <td className="d-none d-md-table-cell" colSpan={5}>
            <ExpandedDetails update={update} />
          </td>
          <td className="d-table-cell d-md-none" colSpan={3}>
            <ExpandedDetails update={update} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedDetails({ update }: { update: WelderUpdate }) {
  return (
    <div className="ms-1">
      <div className="bg-light p-1">Update Details</div>
      <div className="p-1">
        <dl>
          {update.testType && (
            <>
              <dt>Test type</dt>
              <dd>{update.testType}</dd>
            </>
          )}
          {update.ndeMethod && (
            <>
              <dt>Examination</dt>
              <dd>{update.ndeMethod}</dd>
            </>
          )}
          {update.acceptanceCriteria && (
            <>
              <dt className="d-block d-md-none">Criteria</dt>
              <dd className="d-block d-md-none">{update.acceptanceCriteria}</dd>
            </>
          )}
          <dt className="d-block d-md-none">Result</dt>
          <dd className="d-block d-md-none">{update.result === TestResult.Accept ? "Pass" : "Fail"}</dd>
          {update.weldDate && (
            <>
              <dt className="d-block d-md-none">Weld date</dt>
              <dd className="d-block d-md-none">{DateTime.getLocalDate(update.weldDate)}</dd>
            </>
          )}
          {update.ndeDate && (
            <>
              <dt>NDE date</dt>
              <dd>{DateTime.getLocalDate(update.ndeDate)}</dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}
